// Editor de email por bloques: paleta (Contenido / Filas / Cuerpo / Auditar), lienzo
// interactivo (seleccionar, mover, duplicar, eliminar, arrastrar) y panel de propiedades.
// Deshacer/rehacer y vista previa escritorio/móvil con el HTML real renderizado en el servidor.
import type { CSSProperties, DragEvent, JSX, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle, AlertTriangle, ArrowDown, ArrowUp, Code2, Columns3, Copy, Eye, Heading, ImageIcon, Info, Minus, Monitor,
  MousePointerClick, MoveVertical, QrCode, Redo2, Share2, Smartphone, Trash2, Type, Undo2, X,
} from "lucide-react";
import { api } from "../../../lib/api.ts";
import { Button, Loading } from "../../../components/ui.tsx";
import {
  addBlock, addRow, duplicateBlock, duplicateRow, findBlock, moveBlock, moveRow, relocateBlock, removeBlock, removeRow,
  setRowLayout, updateBlock, updateRow,
} from "./designOps.ts";
import { BlockProps, BodyProps, RowProps } from "./EditorPanels.tsx";
import { EmailFrame } from "./EmailFrame.tsx";
import { cleanRichHtml } from "./richtext.ts";
import {
  type AuditIssue, type Block, BLOCK_LABEL, type BlockType, type EmailDesign, FONT_OPTIONS, ROW_LAYOUTS, type Row, SOCIAL_META,
} from "./types.ts";

type Sel = { kind: "block"; id: string } | { kind: "row"; id: string } | null;
type Tab = "content" | "rows" | "body" | "audit";

const PALETTE: Array<{ type: BlockType; icon: ReactNode }> = [
  { type: "heading", icon: <Heading size={18} /> },
  { type: "paragraph", icon: <Type size={18} /> },
  { type: "image", icon: <ImageIcon size={18} /> },
  { type: "button", icon: <MousePointerClick size={18} /> },
  { type: "divider", icon: <Minus size={18} /> },
  { type: "spacer", icon: <MoveVertical size={18} /> },
  { type: "qr", icon: <QrCode size={18} /> },
  { type: "social", icon: <Share2 size={18} /> },
  { type: "html", icon: <Code2 size={18} /> },
];
const DT_NEW = "application/x-mkt-new-block";
const DT_MOVE = "application/x-mkt-move-block";

export interface EditorMeta { subject?: string | null; preheader?: string | null; fromName?: string | null }

export function EmailEditor({ design, onChange, meta }: { design: EmailDesign; onChange: (d: EmailDesign) => void; meta: EditorMeta }): JSX.Element {
  const [sel, setSel] = useState<Sel>(null);
  const [tab, setTab] = useState<Tab>("content");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [preview, setPreview] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditIssue[] | null>(null);

  // ── Historial (deshacer/rehacer). Cambios seguidos del mismo campo se agrupan. ──
  const past = useRef<EmailDesign[]>([]);
  const future = useRef<EmailDesign[]>([]);
  const lastKey = useRef<{ key: string; at: number } | null>(null);
  const [, force] = useState(0);
  const commit = useCallback((next: EmailDesign, key?: string): void => {
    const now = Date.now();
    const grouped = key && lastKey.current?.key === key && now - lastKey.current.at < 1200;
    if (!grouped) { past.current.push(design); if (past.current.length > 100) past.current.shift(); }
    future.current = [];
    lastKey.current = key ? { key, at: now } : null;
    onChange(next); force((n) => n + 1);
  }, [design, onChange]);
  const undo = useCallback((): void => {
    const prev = past.current.pop(); if (!prev) return;
    future.current.push(design); lastKey.current = null; onChange(prev); force((n) => n + 1);
  }, [design, onChange]);
  const redo = useCallback((): void => {
    const nxt = future.current.pop(); if (!nxt) return;
    past.current.push(design); lastKey.current = null; onChange(nxt); force((n) => n + 1);
  }, [design, onChange]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // ── Render del servidor (vista previa exacta y auditoría) ──
  const render = useMutation({
    mutationFn: () => api.post<{ html: string; audit: AuditIssue[] }>("/mkt/campaigns/render", {
      design, subject: meta.subject ?? null, preheader: meta.preheader ?? null, fromName: meta.fromName ?? null,
    }),
  });
  const runAudit = (): void => { render.mutate(undefined, { onSuccess: (r) => setAudit(r.audit) }); };
  const openPreview = (): void => { render.mutate(undefined, { onSuccess: (r) => { setPreview(r.html); setAudit(r.audit); } }); };
  useEffect(() => { if (tab === "audit") runAudit(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Acciones ──
  const selectedRowCol = (): { rowId: string; colId: string } | null => {
    if (sel?.kind === "block") {
      for (const r of design.rows) for (const c of r.columns) if (c.blocks.some((b) => b.id === sel.id)) return { rowId: r.id, colId: c.id };
    }
    if (sel?.kind === "row") { const r = design.rows.find((x) => x.id === sel.id); if (r) return { rowId: r.id, colId: r.columns[0]!.id }; }
    return null;
  };
  const insertBlock = (type: BlockType, target?: { rowId: string; colId: string; index?: number }): void => {
    let t = target ?? selectedRowCol();
    if (!target && t && sel?.kind === "block") {
      // Tras el bloque seleccionado.
      const col = design.rows.find((r) => r.id === t!.rowId)?.columns.find((c) => c.id === t!.colId);
      const i = col?.blocks.findIndex((b) => b.id === sel.id) ?? -1;
      t = { ...t, index: i + 1 } as typeof t;
    }
    const { design: d, id } = addBlock(design, type, t);
    commit(d); setSel({ kind: "block", id });
  };
  const onDrop = (e: DragEvent, target: { rowId: string; colId: string; index: number }): void => {
    e.preventDefault(); e.stopPropagation();
    const nt = e.dataTransfer.getData(DT_NEW) as BlockType;
    const mv = e.dataTransfer.getData(DT_MOVE);
    if (nt) insertBlock(nt, target);
    else if (mv) { commit(relocateBlock(design, mv, target)); setSel({ kind: "block", id: mv }); }
  };

  const selBlock = sel?.kind === "block" ? findBlock(design, sel.id) : null;
  const selRow = sel?.kind === "row" ? design.rows.find((r) => r.id === sel.id) ?? null : null;
  const font = FONT_OPTIONS.find((f) => f.key === design.body.font)?.css ?? "Arial, sans-serif";
  const width = device === "mobile" ? 375 : design.body.width;

  const tabs: Array<[Tab, string]> = [["content", "Contenido"], ["rows", "Filas"], ["body", "Cuerpo"], ["audit", "Auditar"]];

  return (
    <div className="flex flex-col gap-3">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center gap-2">
        <Button small onClick={undo} disabled={!past.current.length} title="Deshacer (Ctrl+Z)"><Undo2 size={14} /></Button>
        <Button small onClick={redo} disabled={!future.current.length} title="Rehacer (Ctrl+Shift+Z)"><Redo2 size={14} /></Button>
        <div className="inline-flex rounded-md border border-line overflow-hidden ml-1">
          <button type="button" className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${device === "desktop" ? "bg-primary-soft text-primary" : "text-ink-secondary hover:bg-surface-hover"}`} onClick={() => setDevice("desktop")}><Monitor size={14} />Escritorio</button>
          <button type="button" className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${device === "mobile" ? "bg-primary-soft text-primary" : "text-ink-secondary hover:bg-surface-hover"}`} onClick={() => setDevice("mobile")}><Smartphone size={14} />Móvil</button>
        </div>
        <div className="ml-auto" />
        <Button small onClick={openPreview} disabled={render.isPending}><Eye size={14} className="mr-1 inline" />Vista previa real</Button>
      </div>

      <div className="grid gap-3 lg:grid-cols-[240px_minmax(0,1fr)_300px]">
        {/* Panel izquierdo */}
        <div className="card p-0 self-start lg:sticky lg:top-4">
          <div className="grid grid-cols-4 border-b border-line text-xs">
            {tabs.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`py-2 border-b-2 -mb-px ${tab === k ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary hover:text-ink"}`}>{label}</button>
            ))}
          </div>
          <div className="p-3">
            {tab === "content" && (
              <>
                <p className="text-xs text-ink-muted mb-2">Arrastra un bloque al lienzo o haz clic para añadirlo {sel ? "después de la selección" : "al final"}.</p>
                <div className="grid grid-cols-3 gap-2">
                  {PALETTE.map((p) => (
                    <button key={p.type} type="button" draggable onDragStart={(e) => { e.dataTransfer.setData(DT_NEW, p.type); e.dataTransfer.effectAllowed = "copy"; }}
                      onClick={() => insertBlock(p.type)}
                      className="flex flex-col items-center gap-1 rounded-md border border-line px-1 py-2.5 text-[11px] text-ink-secondary hover:border-primary hover:text-primary cursor-grab">
                      {p.icon}<span className="leading-tight text-center">{BLOCK_LABEL[p.type]}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {tab === "rows" && (
              <div className="space-y-2">
                <p className="text-xs text-ink-muted">Añade una fila con columnas {sel ? "después de la selección" : "al final"}.</p>
                {ROW_LAYOUTS.map((l) => (
                  <button key={l.label} type="button" className="w-full rounded-md border border-line p-2 hover:border-primary text-left"
                    onClick={() => {
                      const after = sel?.kind === "row" ? sel.id : selectedRowCol()?.rowId;
                      const { design: d, id } = addRow(design, l.ratios, after);
                      commit(d); setSel({ kind: "row", id });
                    }}>
                    <div className="flex gap-1 h-6">{l.ratios.map((r, i) => <div key={i} className="bg-primary-soft border border-primary/30 rounded-sm" style={{ flex: r }} />)}</div>
                    <div className="text-[11px] text-ink-secondary mt-1">{l.label}</div>
                  </button>
                ))}
              </div>
            )}
            {tab === "body" && <BodyProps body={design.body} onChange={(p) => commit({ ...design, body: { ...design.body, ...p } }, `body:${Object.keys(p).join()}`)} />}
            {tab === "audit" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between"><span className="text-xs text-ink-muted">Revisión del correo</span><Button small onClick={runAudit} disabled={render.isPending}>Revisar</Button></div>
                {render.isPending && !audit ? <Loading /> : !audit ? null : audit.length === 0
                  ? <p className="text-sm text-success">Sin problemas detectados.</p>
                  : audit.map((i, k) => (
                    <button key={k} type="button" disabled={!i.blockId} onClick={() => i.blockId && setSel({ kind: "block", id: i.blockId })}
                      className="w-full text-left flex gap-2 rounded-md border border-line p-2 text-xs hover:bg-surface-hover disabled:hover:bg-transparent">
                      {i.level === "error" ? <AlertCircle size={14} className="text-danger shrink-0 mt-0.5" /> : i.level === "warning" ? <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" /> : <Info size={14} className="text-primary shrink-0 mt-0.5" />}
                      <span className="text-ink-secondary">{i.message}</span>
                    </button>
                  ))}
                {render.isError && <p className="text-xs text-danger">{render.error.message}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Lienzo */}
        <div className="rounded-lg border border-line overflow-x-auto" style={{ background: design.body.bg }} onClick={() => setSel(null)}>
          <div className="py-6 px-2 min-h-[420px]">
            <div style={{ width, maxWidth: "100%", margin: design.body.align === "left" && device === "desktop" ? "0" : "0 auto", background: design.body.contentBg, color: design.body.textColor, fontFamily: font }}
              className="shadow-sm transition-[width] duration-200">
              {design.rows.length === 0 && (
                <div className="p-10 text-center text-sm" style={{ color: "#94a3b8" }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const t = e.dataTransfer.getData(DT_NEW) as BlockType; if (t) insertBlock(t, undefined); }}>
                  El correo está vacío. Añade una fila o un bloque desde el panel izquierdo.
                </div>
              )}
              {design.rows.map((row, ri) => (
                <RowView key={row.id} row={row} mobile={device === "mobile"} design={design} sel={sel} setSel={setSel}
                  isFirst={ri === 0} isLast={ri === design.rows.length - 1}
                  onRowAction={(a) => {
                    if (a === "up" || a === "down") commit(moveRow(design, row.id, a === "up" ? -1 : 1));
                    else if (a === "dup") commit(duplicateRow(design, row.id));
                    else { commit(removeRow(design, row.id)); setSel(null); }
                  }}
                  onBlockAction={(id, a) => {
                    if (a === "up" || a === "down") commit(moveBlock(design, id, a === "up" ? -1 : 1));
                    else if (a === "dup") { const r = duplicateBlock(design, id); commit(r.design); if (r.newId) setSel({ kind: "block", id: r.newId }); }
                    else { commit(removeBlock(design, id)); setSel(null); }
                  }}
                  onDrop={onDrop} />
              ))}
            </div>
            <div className="text-center text-[11px] mt-4" style={{ color: "#94a3b8" }}>Pie con enlace de baja (se añade automáticamente al enviar)</div>
          </div>
        </div>

        {/* Propiedades */}
        <div className="card p-4 self-start lg:sticky lg:top-4 max-h-[calc(100vh-2rem)] overflow-y-auto scrollbar-thin">
          {selBlock ? (
            <BlockProps key={selBlock.id} block={selBlock} onChange={(p) => commit(updateBlock(design, selBlock.id, p), `${selBlock.id}:${Object.keys(p).join()}`)} />
          ) : selRow ? (
            <RowProps row={selRow} onChange={(p) => commit(updateRow(design, selRow.id, p), `${selRow.id}:${Object.keys(p).join()}`)}
              onLayout={(ratios) => commit(setRowLayout(design, selRow.id, ratios))} />
          ) : (
            <div className="text-sm text-ink-muted space-y-2">
              <p>Selecciona un bloque o una fila en el lienzo para editar sus propiedades.</p>
              <p className="text-xs">Consejo: usa las flechas del bloque para moverlo, o arrástralo a otra columna.</p>
            </div>
          )}
        </div>
      </div>

      {preview !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Vista previa" onMouseDown={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="card w-full max-w-5xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <div className="min-w-0">
                <div className="font-semibold text-ink truncate">{meta.subject || "(sin asunto)"}</div>
                <div className="text-xs text-ink-muted truncate">{meta.fromName || "Remitente"} · {meta.preheader || "sin preencabezado"}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md border border-line overflow-hidden">
                  <button type="button" className={`px-2 py-1 ${device === "desktop" ? "bg-primary-soft text-primary" : "text-ink-secondary"}`} onClick={() => setDevice("desktop")} title="Escritorio"><Monitor size={14} /></button>
                  <button type="button" className={`px-2 py-1 ${device === "mobile" ? "bg-primary-soft text-primary" : "text-ink-secondary"}`} onClick={() => setDevice("mobile")} title="Móvil"><Smartphone size={14} /></button>
                </div>
                <button className="btn-ghost btn-sm" onClick={() => setPreview(null)} aria-label="Cerrar"><X size={16} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-canvas p-4">
              <EmailFrame html={preview} width={device === "mobile" ? 375 : "100%"} minHeight={500} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────── Lienzo: filas y bloques ──────────
type RowAction = "up" | "down" | "dup" | "del";

function Tools({ children, label }: { children: ReactNode; label: string }): JSX.Element {
  return (
    <div className="absolute -top-3 right-1 z-10 flex items-center gap-0.5 rounded-md bg-primary text-white shadow-md px-1 py-0.5 text-[10px]" onClick={(e) => e.stopPropagation()}>
      <span className="px-1 font-medium">{label}</span>{children}
    </div>
  );
}
const ToolBtn = ({ title, onClick, children, disabled }: { title: string; onClick: () => void; children: ReactNode; disabled?: boolean }): JSX.Element => (
  <button type="button" title={title} disabled={disabled} onClick={onClick} className="p-1 rounded hover:bg-white/20 disabled:opacity-40">{children}</button>
);

function RowView({ row, mobile, design, sel, setSel, isFirst, isLast, onRowAction, onBlockAction, onDrop }: {
  row: Row; mobile: boolean; design: EmailDesign; sel: Sel; setSel: (s: Sel) => void; isFirst: boolean; isLast: boolean;
  onRowAction: (a: RowAction) => void; onBlockAction: (id: string, a: RowAction) => void;
  onDrop: (e: DragEvent, t: { rowId: string; colId: string; index: number }) => void;
}): JSX.Element {
  const active = sel?.kind === "row" && sel.id === row.id;
  const total = row.ratios.reduce((a, b) => a + b, 0);
  const stack = mobile && row.stackOnMobile !== false;
  return (
    <div className={`relative group/row ${active ? "outline outline-2 outline-primary" : "hover:outline hover:outline-1 hover:outline-primary/40"}`}
      style={{ padding: row.padding, background: row.bg ?? undefined }}
      onClick={(e) => { e.stopPropagation(); setSel({ kind: "row", id: row.id }); }}>
      {active && (
        <Tools label="Fila">
          <ToolBtn title="Subir fila" disabled={isFirst} onClick={() => onRowAction("up")}><ArrowUp size={12} /></ToolBtn>
          <ToolBtn title="Bajar fila" disabled={isLast} onClick={() => onRowAction("down")}><ArrowDown size={12} /></ToolBtn>
          <ToolBtn title="Duplicar fila" onClick={() => onRowAction("dup")}><Copy size={12} /></ToolBtn>
          <ToolBtn title="Eliminar fila" onClick={() => onRowAction("del")}><Trash2 size={12} /></ToolBtn>
        </Tools>
      )}
      {!active && <Columns3 size={12} className="absolute left-1 top-1 opacity-0 group-hover/row:opacity-60" style={{ color: "#64748b" }} />}
      <div style={{ display: "flex", flexDirection: stack ? "column" : "row" }}>
        {row.columns.map((col, ci) => (
          <div key={col.id} style={{ flex: stack ? "1 1 auto" : `${(row.ratios[ci] ?? 1) / total} 1 0`, minWidth: 0, minHeight: 40 }}
            className={col.blocks.length ? "" : "border border-dashed border-slate-300 m-1 rounded"}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => onDrop(e, { rowId: row.id, colId: col.id, index: col.blocks.length })}>
            {col.blocks.length === 0 && <div className="text-[11px] text-center py-3" style={{ color: "#94a3b8" }}>Suelta un bloque aquí</div>}
            {col.blocks.map((b, bi) => {
              const isSel = sel?.kind === "block" && sel.id === b.id;
              return (
                <div key={b.id} draggable onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData(DT_MOVE, b.id); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDrop(e, { rowId: row.id, colId: col.id, index: bi })}
                  onClick={(e) => { e.stopPropagation(); setSel({ kind: "block", id: b.id }); }}
                  className={`relative cursor-pointer ${isSel ? "outline outline-2 outline-accent z-[1]" : "hover:outline hover:outline-1 hover:outline-accent/60"}`}>
                  {isSel && (
                    <Tools label={BLOCK_LABEL[b.type]}>
                      <ToolBtn title="Subir" onClick={() => onBlockAction(b.id, "up")}><ArrowUp size={12} /></ToolBtn>
                      <ToolBtn title="Bajar" onClick={() => onBlockAction(b.id, "down")}><ArrowDown size={12} /></ToolBtn>
                      <ToolBtn title="Duplicar" onClick={() => onBlockAction(b.id, "dup")}><Copy size={12} /></ToolBtn>
                      <ToolBtn title="Eliminar" onClick={() => onBlockAction(b.id, "del")}><Trash2 size={12} /></ToolBtn>
                    </Tools>
                  )}
                  <BlockView block={b} design={design} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Aproximación visual del bloque (la vista previa real la genera el servidor). */
function BlockView({ block: b, design }: { block: Block; design: EmailDesign }): JSX.Element {
  const p = "padding" in b ? b.padding ?? 10 : 0;
  const wrap = (children: ReactNode, style?: CSSProperties): JSX.Element => <div style={{ padding: p, ...style }}>{children}</div>;
  switch (b.type) {
    case "heading": {
      const size = b.fontSize ?? (b.level === 1 ? 28 : b.level === 2 ? 22 : 18);
      return wrap(<div style={{ fontSize: size, fontWeight: 700, lineHeight: 1.25, color: b.color ?? design.body.textColor, textAlign: b.align, whiteSpace: "pre-wrap" }}>{b.text || <span style={{ opacity: 0.4 }}>Encabezado vacío</span>}</div>);
    }
    case "paragraph":
      return wrap(<div className="[&_p]:my-[0.8em] [&_a]:underline [&_a]:text-[var(--lc)] [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
        style={{ fontSize: b.fontSize ?? 15, lineHeight: 1.55, color: b.color ?? design.body.textColor, textAlign: b.align, ["--lc" as string]: design.body.linkColor } as CSSProperties}
        // Contenido saneado con lista blanca (cleanRichHtml) antes de insertarlo.
        dangerouslySetInnerHTML={{ __html: cleanRichHtml(b.html) || "<p style=\"opacity:.4\">Párrafo vacío</p>" }} />);
    case "image":
      return wrap(b.src
        ? <img src={b.src} alt={b.alt} style={{ display: "block", width: `${b.width}%`, height: "auto", margin: b.align === "center" ? "0 auto" : b.align === "right" ? "0 0 0 auto" : 0 }} />
        : <div style={{ border: "2px dashed #cbd5e1", padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Imagen — elige una en el panel derecho</div>);
    case "button":
      return wrap(
        <div style={{ textAlign: b.align }}>
          <span style={{ display: b.fullWidth ? "block" : "inline-block", background: b.bg, color: b.color, borderRadius: b.radius, padding: "12px 26px", fontWeight: 700, fontSize: b.fontSize ?? 15, textAlign: "center" }}>{b.text || "Botón"}</span>
        </div>);
    case "divider":
      return wrap(<div style={{ borderTop: `${b.thickness}px ${b.style} ${b.color}`, width: `${b.width}%`, margin: "0 auto" }} />);
    case "spacer":
      return <div style={{ height: b.height, background: "repeating-linear-gradient(45deg,transparent,transparent 6px,rgba(148,163,184,.12) 6px,rgba(148,163,184,.12) 12px)" }} />;
    case "qr":
      return wrap(
        <div style={{ textAlign: b.align }}>
          {b.data.trim()
            ? <img src={`/api/v1/mkt/qr.png?d=${encodeURIComponent(b.data)}&c=${b.color.replace("#", "")}`} alt="Código QR" width={b.size} height={b.size} style={{ display: "inline-block", width: b.size, height: b.size }} />
            : <span style={{ color: "#94a3b8", fontSize: 12 }}>QR sin contenido</span>}
        </div>);
    case "html":
      return <EmailFrame html={`<!doctype html><meta charset="utf-8"><body style="margin:0;font-family:${FONT_OPTIONS.find((f) => f.key === design.body.font)?.css ?? "Arial"};color:${design.body.textColor}">${b.html}</body>`} minHeight={30} title="Bloque HTML" />;
    case "social":
      return wrap(
        <div style={{ textAlign: b.align }}>
          {b.items.map((it, i) => {
            const s = SOCIAL_META[it.network];
            return <span key={i} title={s.name} style={{ display: "inline-block", width: b.size, height: b.size, lineHeight: `${b.size}px`, borderRadius: "50%", background: s.bg, color: "#fff", fontSize: Math.round(b.size * (s.label.length > 2 ? 0.32 : 0.42)), fontWeight: 700, textAlign: "center", margin: "0 4px", fontFamily: "Arial,sans-serif" }}>{s.label}</span>;
          })}
        </div>);
  }
}
